import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { Add, Autorenew, Remove, Restaurant } from "@mui/icons-material";
import { AllergenChip } from "@/components/ui/AllergenChip";

export interface OrderMenuProductAllergen {
  id: string;
  name: string;
  code: string;
  icon: string | null;
}

export interface OrderMenuProductCardProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  imageUrl: string | null;
  isVegetarian: boolean;
  isPremium: boolean;
  isSpicy: boolean;
}

interface OrderMenuProductCardProps {
  product: OrderMenuProductCardProduct;
  quantity: number;
  allergens?: OrderMenuProductAllergen[];
  /** Allergènes déclarés par le client (surlignage des conflits). */
  customerAllergenIds?: string[];
  onAdd: () => void;
  onRemove: () => void;
  /** Produit épuisé (plus aucune portion disponible). */
  exhausted?: boolean;
  /** Impossible d'ajouter une unité (plafond atteint). */
  addDisabled?: boolean;
  disabled?: boolean;
  /** Plat éligible à la formule Switch (accompagnement remplaçable, sans surcoût). */
  switchEligible?: boolean;
  /** La formule Switch est activée pour ce produit dans le panier. */
  switchSelected?: boolean;
  /** Bascule l'activation de la formule Switch. */
  onToggleSwitch?: () => void;
}

/**
 * Carte produit du parcours commande (backoffice et portail client).
 * Alignée sur la disposition de `/orders/new`.
 */
export function OrderMenuProductCard({
  product,
  quantity,
  allergens = [],
  customerAllergenIds = [],
  onAdd,
  onRemove,
  exhausted = false,
  addDisabled = false,
  disabled = false,
  switchEligible = false,
  switchSelected = false,
  onToggleSwitch,
}: OrderMenuProductCardProps): JSX.Element {
  const cannotAdd = disabled || addDisabled || exhausted;
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: "background.paper",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        border: "1px solid",
        borderColor: "transparent",
        display: "flex",
        gap: 2,
        alignItems: "flex-start",
        transition: "all 0.2s",
        opacity: disabled || exhausted ? 0.6 : 1,
        "&:hover": disabled || exhausted
          ? {}
          : {
              borderColor: "rgba(231, 126, 35, 0.3)",
            },
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          flexShrink: 0,
          borderRadius: "8px",
          bgcolor: "grey.100",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...(product.imageUrl && {
            backgroundImage: `url(${product.imageUrl})`,
          }),
        }}
      >
        {!product.imageUrl ? <Restaurant sx={{ color: "grey.400", fontSize: 32 }} /> : null}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: 0.75,
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: "14px",
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              {product.name}
            </Typography>
            {exhausted ? (
              <Box
                component="span"
                sx={{
                  fontSize: "10px",
                  px: 0.75,
                  py: 0.25,
                  bgcolor: "grey.200",
                  color: "text.secondary",
                  borderRadius: "4px",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                Épuisé
              </Box>
            ) : null}
          </Box>
          <Typography
            sx={{
              fontFamily: "monospace",
              fontWeight: 700,
              color: "secondary.main",
              fontSize: "14px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {product.price.toFixed(2)}€
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: "text.secondary",
            mt: 0.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.4,
          }}
        >
          {product.description || "Aucune description"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
          {switchEligible ? (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                fontSize: "10px",
                px: 0.75,
                py: 0.25,
                bgcolor: "#dcfce7",
                color: "#15803d",
                borderRadius: "4px",
                border: "1px solid #bbf7d0",
                fontWeight: 700,
              }}
            >
              <Autorenew sx={{ fontSize: 12 }} />
              Switch
            </Box>
          ) : null}
          {product.isVegetarian ? (
            <Box
              component="span"
              sx={{
                fontSize: "10px",
                px: 0.75,
                py: 0.25,
                bgcolor: "#dcfce7",
                color: "#15803d",
                borderRadius: "4px",
                border: "1px solid #bbf7d0",
              }}
            >
              Végétarien
            </Box>
          ) : null}
          {product.isPremium ? (
            <Box
              component="span"
              sx={{
                fontSize: "10px",
                px: 0.75,
                py: 0.25,
                bgcolor: "#fef9c3",
                color: "#a16207",
                borderRadius: "4px",
                border: "1px solid #fef08a",
              }}
            >
              Premium
            </Box>
          ) : null}
          {product.isSpicy ? (
            <Box
              component="span"
              sx={{
                fontSize: "10px",
                px: 0.75,
                py: 0.25,
                bgcolor: "#fee2e2",
                color: "#b91c1c",
                borderRadius: "4px",
                border: "1px solid #fecaca",
              }}
            >
              Épicé
            </Box>
          ) : null}
        </Box>
        {switchEligible && quantity > 0 && onToggleSwitch ? (
          <FormControlLabel
            sx={{ mt: 0.5, ml: 0 }}
            control={
              <Switch
                size="small"
                checked={switchSelected}
                onChange={onToggleSwitch}
                color="success"
              />
            }
            label={
              <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                Formule Switch (3/4 légumes, 1/4 féculents)
              </Typography>
            }
          />
        ) : null}
        {allergens.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
            {allergens.map((allergen) => {
              const isConflict = customerAllergenIds.includes(allergen.id);
              const label = allergen.icon
                ? `${allergen.icon} ${allergen.name}`
                : allergen.name;
              return (
                <AllergenChip
                  key={allergen.id}
                  label={label}
                  compact
                  sx={
                    isConflict
                      ? {
                          bgcolor: "error.50",
                          color: "error.dark",
                          borderColor: "error.main",
                          fontWeight: 700,
                        }
                      : undefined
                  }
                />
              );
            })}
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          bgcolor: "grey.50",
          borderRadius: "8px",
          p: 0.5,
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        <Box
          component="button"
          type="button"
          disabled={cannotAdd}
          onClick={onAdd}
          aria-label="Ajouter"
          sx={{
            width: 28,
            height: 28,
            borderRadius: "4px",
            bgcolor: cannotAdd ? "grey.200" : "white",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: cannotAdd ? "grey.500" : "secondary.main",
            border: "none",
            cursor: cannotAdd ? "not-allowed" : "pointer",
            transition: "all 0.15s",
            "&:hover": cannotAdd ? {} : { bgcolor: "grey.50" },
            "&:active": cannotAdd ? {} : { transform: "scale(0.95)" },
          }}
        >
          <Add sx={{ fontSize: 16 }} />
        </Box>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            width: 24,
            textAlign: "center",
            color: quantity > 0 ? "text.primary" : "grey.300",
          }}
        >
          {quantity}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => quantity > 0 && onRemove()}
          aria-label="Retirer"
          sx={{
            width: 28,
            height: 28,
            borderRadius: "4px",
            bgcolor: "white",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: quantity > 0 ? "grey.400" : "grey.300",
            border: "none",
            cursor: quantity > 0 ? "pointer" : "default",
            transition: "all 0.15s",
            pointerEvents: quantity === 0 ? "none" : "auto",
            "&:hover": quantity > 0 ? { bgcolor: "grey.50", color: "secondary.main" } : {},
            "&:active": quantity > 0 ? { transform: "scale(0.95)" } : {},
          }}
        >
          <Remove sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    </Box>
  );
}
